import { runTransaction, setDoc, updateDoc } from "firebase/firestore";
import { getDb } from "@/lib/firebase/client";
import { paths, refDoc } from "./paths";
import {
  userPrefsSchema,
  type TreasureRewardId,
  type TreasureSlot,
  type UserPrefs,
} from "@/lib/schemas";
import {
  normalizeTreasurePrefs,
  treasureRewardById,
} from "@/lib/utils/treasure";

/**
 * Patch user preferences (theme + accent). Trzymamy w users/{uid}.prefs.
 * setDoc z merge — żeby nie zwalić householdId/role.
 */
export async function updateUserPrefs(
  uid: string,
  patch: Partial<UserPrefs>,
): Promise<void> {
  const db = getDb();
  await setDoc(refDoc(db, paths.user(uid)), { prefs: patch }, { merge: true });
}

/**
 * Zapisuje że dany mascot moment został wyświetlony i zamknięty przez
 * użytkownika. Klucz formatu "context:variant" — np. "level-up:5",
 * "badge:streak-7", "welcome:1".
 *
 * Używa update z dot-notation żeby nie nadpisać innych seenMoments.
 */
export async function markMomentSeen(uid: string, key: string): Promise<void> {
  const db = getDb();
  await updateDoc(refDoc(db, paths.user(uid)), {
    [`prefs.seenMoments.${key}`]: new Date().toISOString(),
  });
}

/**
 * Analog markMomentSeen, ale dla **kliknięć** w linki zewnętrzne (CTR).
 * seenMoments mówi „użytkownik to zobaczył", clickedMoments — „kliknął".
 * Ratio z obu daje CTR w panelu admin. Klucz formatu "blog:{slug}" /
 * "philosophy-link".
 *
 * Idempotentny — kolejne kliknięcie nadpisuje timestamp (ostatnie wygrywa).
 */
export async function markMomentClicked(
  uid: string,
  key: string,
): Promise<void> {
  const db = getDb();
  await updateDoc(refDoc(db, paths.user(uid)), {
    [`prefs.clickedMoments.${key}`]: new Date().toISOString(),
  });
}

/**
 * Otwiera dokładnie jedną zdobytą skrzynkę i zapisuje wylosowany kosmetyk.
 * `earnedChestCount` jest liczony z approved completions ze zdjęciem.
 * Transakcja chroni zwykły flow przed podwójnym tapem / dwoma urządzeniami;
 * stan pozostaje wyłącznie kosmetyczny i nigdy nie jest źródłem XP ani salda.
 */
export async function claimTreasureReward(
  uid: string,
  earnedChestCount: number,
  rewardId: TreasureRewardId,
): Promise<void> {
  const db = getDb();
  const userRef = refDoc(db, paths.user(uid));

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(userRef);
    if (!snap.exists()) throw new Error("Nie znaleziono profilu");

    const parsed = userPrefsSchema.safeParse(snap.data().prefs);
    const current = normalizeTreasurePrefs(
      parsed.success ? parsed.data.treasure : undefined,
    );
    if (current.openedChestCount >= earnedChestCount) {
      throw new Error("Ta skrzynka została już otwarta");
    }
    if (current.ownedRewardIds.includes(rewardId)) {
      throw new Error("Ten skarb jest już w Twojej kolekcji");
    }
    const reward = treasureRewardById(rewardId);
    if (!reward) throw new Error("Nieznany skarb");

    tx.update(userRef, {
      "prefs.treasure.openedChestCount": current.openedChestCount + 1,
      "prefs.treasure.ownedRewardIds": [...current.ownedRewardIds, rewardId],
      "prefs.treasure.equipped": {
        ...(current.equipped ?? {}),
        [reward.slot]: rewardId,
      },
    });
  });
}

/** Zmienia aktywny kosmetyk. Można założyć tylko już zdobyty skarb. */
export async function equipTreasureReward(
  uid: string,
  rewardId: TreasureRewardId,
): Promise<void> {
  const db = getDb();
  const userRef = refDoc(db, paths.user(uid));

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(userRef);
    if (!snap.exists()) throw new Error("Nie znaleziono profilu");

    const parsed = userPrefsSchema.safeParse(snap.data().prefs);
    const current = normalizeTreasurePrefs(
      parsed.success ? parsed.data.treasure : undefined,
    );
    if (!current.ownedRewardIds.includes(rewardId)) {
      throw new Error("Najpierw zdobądź ten skarb");
    }
    const reward = treasureRewardById(rewardId);
    if (!reward) throw new Error("Nieznany skarb");

    tx.update(userRef, {
      "prefs.treasure.equipped": {
        ...(current.equipped ?? {}),
        [reward.slot]: rewardId,
      },
    });
  });
}

/** Zdejmuje przedmiot z jednego slotu, nie usuwając go z kolekcji. */
export async function unequipTreasureSlot(
  uid: string,
  slot: TreasureSlot,
): Promise<void> {
  const db = getDb();
  const userRef = refDoc(db, paths.user(uid));

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(userRef);
    if (!snap.exists()) throw new Error("Nie znaleziono profilu");

    const parsed = userPrefsSchema.safeParse(snap.data().prefs);
    const current = normalizeTreasurePrefs(
      parsed.success ? parsed.data.treasure : undefined,
    );
    const equipped = { ...(current.equipped ?? {}) };
    delete equipped[slot];

    tx.update(userRef, {
      "prefs.treasure.equipped": equipped,
    });
  });
}
